import type { DependencyList, EffectCallback } from 'react';

import isEqual from 'fast-deep-equal/react';

import { useCompareLayoutEffect } from './compare-layout-effect.hook';

/**
 * A custom React hook that works like `useLayoutEffect`, but performs a deep comparison
 * of the dependencies array to determine whether the effect should re-run.
 *
 * This hook uses `fast-deep-equal` to compare dependencies, ensuring that the effect
 * only runs when the dependencies have actually changed in a deep sense, not just by reference.
 *
 * @param effect - The effect callback function to run after layout and paint.
 * @param dependencies - The list of dependencies to watch for changes, compared deeply.
 *
 * @remarks
 * - Internally uses `useCompareLayoutEffect` with a deep equality function.
 * - Useful when dependencies are objects or arrays that may be recreated but not changed.
 *
 * @see {@link useCompareLayoutEffect}
 */
export const useDeepCompareLayoutEffect = (effect: EffectCallback, dependencies: DependencyList) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are memoized using useCompareLayoutEffect
  useCompareLayoutEffect(effect, dependencies, isEqual);
};
